import { clsx } from 'clsx'
import { type FormEvent, type JSX, useEffect, useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSessionStorage } from 'usehooks-ts'

import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormLabel,
  Input,
  Link,
} from 'base-ui'

import { useOpenableControls } from '../../generic-hooks/useOpenableControls/useOpenableControls'
import { useGlobalConfigContext } from '../../global-config/context/globalConfigContext'
import { gtmDataLayerPush } from '../../gtm/gtmDataLayer'
import { LOCALE } from '../../i18n/LOCALE'
import { StaticImage } from '../../image/StaticImage'
import {
  urlResolverGetNewsletter,
  urlResolverGetNewsletterPage,
} from '../../url-handling/urlResolver'

import { getNewsletterModalHero } from './getNewsletterModalHero'
import { NewsletterModalTitle } from './NewsletterModalTitle'

const NEWSLETTER_MODAL_TIMEOUT = 7000

export const NEWSLETTER_MODAL_ID = 'NewsletterModal'

export const NewsletterModal = (): JSX.Element => {
  const globalConfig = useGlobalConfigContext()
  const intl = useIntl()
  const newsletterUrl = urlResolverGetNewsletter(globalConfig)

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)
  const [, setNewsletterModalShown] = useSessionStorage(
    NEWSLETTER_MODAL_ID,
    false,
    {
      initializeWithValue: false,
    },
  )

  const emailId = useId()
  const titleId = useId()

  useEffect(() => {
    const showPopup = setTimeout(() => {
      gtmDataLayerPush({
        event: 'popin',
        popinName: '__newsletterModal',
      })
      handleOpen()
    }, NEWSLETTER_MODAL_TIMEOUT)

    return (): void => {
      clearTimeout(showPopup)
    }
  }, [handleOpen])

  const onModalClose = (): void => {
    handleClose()
    setNewsletterModalShown(true)
  }

  /*
   * we are delaying the form submission to track the event
   * and to set sessionStorage value
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setNewsletterModalShown(true)
    event.currentTarget.submit()
  }

  const isLocaleItIt = globalConfig.locale === LOCALE.IT_IT

  return (
    <Dialog
      onClose={onModalClose}
      open={isOpen}
      slotProps={{
        dialog: {
          'aria-labelledby': titleId,
          className: 'mobile-sm:w-11/12 max-w-xl bg-transparent h-auto',
        },
      }}
    >
      <NewsletterModalTitle onModalClose={onModalClose} />

      <div
        className={clsx(
          'mobile-sm:rounded-xl mobile-sm:rounded-b-none',
          isLocaleItIt ? 'bg-dark-primary' : 'bg-light-brand-high',
        )}
      >
        <StaticImage
          alt="newsletter"
          className="m-auto"
          src={getNewsletterModalHero(globalConfig)}
        />
      </div>
      <DialogContent className="relative flex flex-col items-center justify-center bg-light-primary-low mobile-sm:rounded-xl mobile-sm:rounded-t-none">
        <div className="flex max-w-[520px] flex-col items-center justify-center overflow-hidden text-center mobile-sm:rounded-xl mobile-sm:rounded-t-none">
          <p className="mb-4 text-m font-medium" id={titleId}>
            <FormattedMessage id="general.newsletterBanner.title" />
          </p>
          <form
            action={newsletterUrl}
            className="flex w-full flex-col items-center"
            method="GET"
            onSubmit={onSubmit}
          >
            <FormControl required>
              <FormLabel htmlFor={emailId}>
                {intl.formatMessage({ id: 'general.label.email' })}
              </FormLabel>
              <Input
                className="w-64 tablet:w-80"
                name="email"
                slotProps={{
                  input: {
                    id: emailId,
                  },
                }}
                type="email"
              />
            </FormControl>
            <Button className="my-3.5" type="submit">
              <FormattedMessage id="checkout.error.requestBody.validation.email.login" />
            </Button>
          </form>
          <Link
            className="text-xs text-dark-primary-strong"
            href={urlResolverGetNewsletterPage(globalConfig)}
          >
            <FormattedMessage id="general.newsletterBanner.conditions" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  )
}
